import React, { useEffect, useMemo, useRef, useState } from 'react';
import { colors } from '../../theme';

const CHART_HEIGHT = 360;
const MARGINS = { top: 32, right: 24, bottom: 56, left: 72 };
const HIGHLIGHT_COLORS = ['#46BB89', '#EC2124', '#2A4B8D', '#FFC107', '#6C63FF'];

const ExportButton = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: '0.4rem 0.75rem',
      marginLeft: '0.5rem',
      borderRadius: '4px',
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.background,
      color: colors.text,
      cursor: 'pointer',
      fontSize: '0.85rem'
    }}
  >
    {children}
  </button>
);

const Tooltip = ({ tooltip, formatQuantity }) => {
  if (!tooltip?.visible) return null;
  return (
    <div
      style={{
        position: 'fixed',
        left: tooltip.x + 16,
        top: tooltip.y + 12,
        backgroundColor: 'rgba(35,35,35,0.95)',
        color: '#FFFFFF',
        padding: '0.6rem 0.8rem',
        borderRadius: '6px',
        fontSize: '0.75rem',
        pointerEvents: 'none',
        zIndex: 999
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{tooltip.monthLabel}</div>
      <div>Total: {formatQuantity(tooltip.total)}</div>
      {tooltip.series.map(item => (
        <div key={item.productNumber} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.15rem' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: item.color }} />
          <span>
            {item.productNumber}: {formatQuantity(item.value)}
          </span>
        </div>
      ))}
      {tooltip.isProjection ? (
        <div style={{ marginTop: '0.4rem', fontStyle: 'italic', opacity: 0.7 }}>Projected month</div>
      ) : null}
    </div>
  );
};

const downloadBlob = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

const svgToPng = (svgElement, width, height, filename) => {
  if (!svgElement) return;
  const cloned = svgElement.cloneNode(true);
  cloned.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  const blob = new Blob(['<?xml version="1.0" standalone="no"?>\n', cloned.outerHTML], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const image = new Image();
  const scale = window.devicePixelRatio || 1;
  image.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = width * scale;
    canvas.height = height * scale;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    ctx.scale(scale, scale);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(image, 0, 0);
    const pngUrl = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = pngUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(pngUrl);
    URL.revokeObjectURL(url);
  };
  image.src = url;
};

const formatQuantityDefault = (value) => value.toLocaleString('en-US', { maximumFractionDigits: 0 });

const ProductionLineChart = ({
  data,
  loading,
  error,
  selectedProducts,
  onClearSelection
}) => {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [width, setWidth] = useState(960);
  const [tooltip, setTooltip] = useState({ visible: false });

  useEffect(() => {
    if (!containerRef.current) return;
    const element = containerRef.current;
    const updateWidth = () => {
      setWidth(element.clientWidth || 960);
    };
    updateWidth();
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateWidth)
      : null;
    if (observer) observer.observe(element);
    const handleResize = () => updateWidth();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (observer) observer.disconnect();
    };
  }, []);

  const chartModel = useMemo(() => {
    if (!data) {
      return null;
    }
    const actualMonths = data.months || [];
    const projectionMonths = data.total?.projection || [];
    const allMonthKeys = [
      ...actualMonths.map(m => m.key),
      ...projectionMonths.map(m => m.month)
    ];
    if (!allMonthKeys.length) {
      return null;
    }
    const monthMetaMap = new Map([
      ...actualMonths.map(m => [m.key, { label: m.label, iso: m.iso, isProjection: false }]),
      ...projectionMonths.map(m => [m.month, { label: m.label, iso: m.iso, isProjection: true }])
    ]);

    const totalLine = data.total?.line || [];
    const totalProjection = data.total?.projection || [];

    const allValues = [
      ...totalLine.map(point => point.qtyProduced),
      ...totalProjection.map(point => point.qtyProduced)
    ];
    data.series?.forEach(series => {
      series.points.forEach(point => {
        allValues.push(point.qtyProduced);
      });
    });
    const maxValue = allValues.length ? Math.max(...allValues) : 0;
    const paddedMax = maxValue <= 0 ? 1 : maxValue * 1.1;

    const usableWidth = Math.max(1, width - MARGINS.left - MARGINS.right);
    const usableHeight = Math.max(1, CHART_HEIGHT - MARGINS.top - MARGINS.bottom);
    const spacing = usableWidth / Math.max(1, allMonthKeys.length - 1);
    const xPositions = new Map();
    allMonthKeys.forEach((key, index) => {
      xPositions.set(key, MARGINS.left + index * spacing);
    });
    const yScale = (value) => {
      const clamped = Math.max(0, value);
      return MARGINS.top + usableHeight - (clamped / paddedMax) * usableHeight;
    };

    const buildPath = (points) => {
      return points.reduce((path, point, index) => {
        const x = xPositions.get(point.month);
        const y = yScale(point.qtyProduced);
        if (x == null || Number.isNaN(y)) return path;
        return index === 0 ? `M${x},${y}` : `${path} L${x},${y}`;
      }, '');
    };

    const highlightSet = new Set(data.highlightedProducts || []);
    const highlightColors = new Map();
    let highlightIndex = 0;

    highlightSet.forEach(productNumber => {
      highlightColors.set(productNumber, HIGHLIGHT_COLORS[highlightIndex % HIGHLIGHT_COLORS.length]);
      highlightIndex += 1;
    });

    const seriesPaths = (data.series || []).map(series => {
      const color = highlightColors.get(series.productNumber) || 'rgba(70,187,137,0.35)';
      const isFocused = !selectedProducts?.length || selectedProducts.includes(series.productNumber);
      return {
        productNumber: series.productNumber,
        path: buildPath(series.points),
        color,
        isFocused,
        average: series.average6m,
        lifetimeTotal: series.lifetimeTotal
      };
    });

    const projectionPathPoints = totalProjection.length
      ? [
          totalLine[totalLine.length - 1],
          ...totalProjection
        ]
      : [];

    return {
      xPositions,
      yScale,
      spacing,
      monthKeys: allMonthKeys,
      monthMetaMap,
      totalPath: buildPath(totalLine),
      projectionPath: projectionPathPoints.length ? buildPath(projectionPathPoints) : '',
      seriesPaths,
      paddedMax,
      usableHeight,
      totalLine,
      totalProjection
    };
  }, [data, width, selectedProducts]);

  const formatQuantity = formatQuantityDefault;

  const handleMouseMove = (event) => {
    if (!chartModel || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const relativeX = event.clientX - rect.left - MARGINS.left;
    const index = Math.round(relativeX / chartModel.spacing);
    const clampedIndex = Math.min(Math.max(index, 0), chartModel.monthKeys.length - 1);
    const key = chartModel.monthKeys[clampedIndex];
    const meta = chartModel.monthMetaMap.get(key) || {};

    const series = (data.series || []).map(seriesItem => {
      const point = seriesItem.points.find(p => p.month === key);
      return {
        productNumber: seriesItem.productNumber,
        value: point ? point.qtyProduced : 0,
        color: chartModel.seriesPaths.find(path => path.productNumber === seriesItem.productNumber)?.color || '#CCCCCC'
      };
    }).filter(item => item.value !== undefined && (!selectedProducts?.length || selectedProducts.includes(item.productNumber)));

    const totalPoint = chartModel.totalLine.find(point => point.month === key);
    const projectionPoint = chartModel.totalProjection.find(point => point.month === key);
    const totalValue = totalPoint?.qtyProduced ?? projectionPoint?.qtyProduced ?? 0;

    setTooltip({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      monthLabel: meta.label || key,
      total: totalValue,
      series,
      isProjection: Boolean(meta.isProjection)
    });
  };

  const handleMouseLeave = () => setTooltip({ visible: false });

  const exportCsv = () => {
    if (!data) return;
    const lines = ['Month,Series,Product,Quantity,Projection'];
    const appendLine = (month, seriesName, product, quantity, projection) => {
      const fields = [month, seriesName, product, quantity, projection ? 'true' : 'false'];
      lines.push(fields.map(value => `"${String(value).replace(/"/g, '""')}"`).join(','));
    };
    data.total?.line?.forEach(point => {
      appendLine(point.label || point.month, 'Total', '', point.qtyProduced, false);
    });
    data.total?.projection?.forEach(point => {
      appendLine(point.label || point.month, 'Total', '', point.qtyProduced, true);
    });
    (data.series || []).forEach(seriesItem => {
      seriesItem.points.forEach(point => {
        appendLine(point.label || point.month, 'Product', seriesItem.productNumber, point.qtyProduced, false);
      });
    });
    downloadBlob(lines.join('\n'), 'production_line_chart.csv', 'text/csv;charset=utf-8;');
  };

  const exportPng = () => {
    if (!svgRef.current) return;
    svgToPng(svgRef.current, width, CHART_HEIGHT, 'production_line_chart.png');
  };

  if (loading) {
    return <div style={{ padding: '1rem' }}>Loading line chart�</div>;
  }

  if (error) {
    return <div style={{ padding: '1rem', color: colors.danger }}>Failed to load line chart data.</div>;
  }

  if (!data || !chartModel) {
    return (
      <div style={{ padding: '1rem' }}>
        <div>No data for the selected range.</div>
        <div style={{ fontSize: '0.85rem', color: colors.muted }}>Adjust filters or the date range and try again.</div>
      </div>
    );
  }

  const tickInterval = Math.max(1, Math.floor(chartModel.monthKeys.length / 10));

  return (
    <div style={{ marginTop: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <div style={{ fontWeight: 600 }}>
          Overlayed Line Chart
          {data?.meta?.dataSource ? (
            <div style={{ fontSize: '0.75rem', color: colors.muted, fontWeight: 400 }}>
              Source: {data.meta.dataSource === 'fallback' ? 'Live aggregation' : 'Cached totals'}
            </div>
          ) : null}
        </div>
        <div>
          <ExportButton onClick={exportCsv}>Export CSV</ExportButton>
          <ExportButton onClick={exportPng}>Export PNG</ExportButton>
        </div>
      </div>
      <div
        ref={containerRef}
        style={{
          width: '100%',
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '0.75rem',
          backgroundColor: '#FFFFFF',
          position: 'relative'
        }}
      >
        <svg
          ref={svgRef}
          width={width}
          height={CHART_HEIGHT}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={{ display: 'block' }}
        >
          <rect x={0} y={0} width={width} height={CHART_HEIGHT} fill="#FFFFFF" />
          <line
            x1={MARGINS.left}
            y1={CHART_HEIGHT - MARGINS.bottom}
            x2={width - MARGINS.right}
            y2={CHART_HEIGHT - MARGINS.bottom}
            stroke="#CCCCCC"
          />
          <line
            x1={MARGINS.left}
            y1={MARGINS.top}
            x2={MARGINS.left}
            y2={CHART_HEIGHT - MARGINS.bottom}
            stroke="#CCCCCC"
          />

          {Array.from({ length: 5 }).map((_, i) => {
            const ratio = i / 4;
            const value = chartModel.paddedMax * ratio;
            const y = MARGINS.top + chartModel.usableHeight - chartModel.usableHeight * ratio;
            return (
              <g key={`grid-${i}`}>
                <line
                  x1={MARGINS.left}
                  y1={y}
                  x2={width - MARGINS.right}
                  y2={y}
                  stroke="#EEEEEE"
                  strokeDasharray="4 4"
                />
                <text
                  x={MARGINS.left - 10}
                  y={y + 4}
                  textAnchor="end"
                  fontSize="11"
                  fill="#666666"
                >
                  {formatQuantity(value)}
                </text>
              </g>
            );
          })}

          {chartModel.monthKeys.map((key, index) => {
            if (index % tickInterval !== 0 && index !== chartModel.monthKeys.length - 1) return null;
            const x = chartModel.xPositions.get(key);
            if (x == null) return null;
            const meta = chartModel.monthMetaMap.get(key) || {};
            return (
              <g key={`tick-${key}`}>
                <line
                  x1={x}
                  y1={CHART_HEIGHT - MARGINS.bottom}
                  x2={x}
                  y2={CHART_HEIGHT - MARGINS.bottom + 6}
                  stroke="#999999"
                />
                <text
                  x={x}
                  y={CHART_HEIGHT - MARGINS.bottom + 20}
                  textAnchor="middle"
                  fontSize="10"
                  fill={meta.isProjection ? '#777777' : '#444444'}
                >
                  {meta.label || key}
                </text>
              </g>
            );
          })}

          {chartModel.seriesPaths.map(path => (
            <path
              key={`series-${path.productNumber}`}
              d={path.path}
              fill="none"
              stroke={path.color}
              strokeWidth={path.isFocused ? 1.5 : 1}
              opacity={path.isFocused ? 0.9 : 0.25}
            />
          ))}

          {chartModel.totalPath && (
            <path
              d={chartModel.totalPath}
              fill="none"
              stroke="#000000"
              strokeWidth={2.5}
            />
          )}
          {chartModel.projectionPath && (
            <path
              d={chartModel.projectionPath}
              fill="none"
              stroke="#000000"
              strokeWidth={2.5}
              strokeDasharray="6 6"
            />
          )}
        </svg>

        <Tooltip tooltip={tooltip} formatQuantity={formatQuantity} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem', alignItems: 'center' }}>
        <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>Highlights:</div>
        {(data.highlightedProducts || []).map((product, index) => (
          <span
            key={product}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              backgroundColor: 'rgba(70,187,137,0.08)',
              padding: '0.2rem 0.6rem',
              borderRadius: '999px',
              border: `1px solid ${colors.border}`,
              fontSize: '0.8rem'
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: HIGHLIGHT_COLORS[index % HIGHLIGHT_COLORS.length]
              }}
            />
            {product}
          </span>
        ))}
      </div>
      {selectedProducts?.length ? (
        <div style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>
          <span>Filtering by: </span>
          {selectedProducts.map(product => (
            <span
              key={`filter-${product}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                backgroundColor: 'rgba(70,187,137,0.12)',
                borderRadius: '12px',
                padding: '0.2rem 0.6rem',
                border: `1px solid ${colors.border}`,
                marginRight: '0.4rem'
              }}
            >
              <span>{product}</span>
            </span>
          ))}
          <button
            type="button"
            onClick={onClearSelection}
            style={{
              marginLeft: '0.5rem',
              background: 'transparent',
              border: 'none',
              color: colors.danger,
              cursor: 'pointer'
            }}
          >
            Clear selection
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default ProductionLineChart;
